import { html, css, LitElement, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { hermesClient } from "../hermes-singleton.js";
import { isEadSystemAdmin } from "../ead-admin.ts";
import {
  compileEadScriptToMermaid,
  countEadScriptElements,
  diagramGeneratePromptFor,
  mermaidFromCurrentEadScript,
} from "../lib/skill-report-markdown.ts";
import { notifyOperator } from "../operator-notify.ts";
import "../components/ead-mermaid-diagram.ts";

// Types for EAD Workflow Rules
interface EADWorkflowRuleSummary {
  id?: number;
  ruleKey: string;
  ruleName: string;
  ruleDescription: string;
  sortNumber: number;
  enabled: boolean;
  modifiedAt: number;
}

interface EADWorkflowRule extends EADWorkflowRuleSummary {
  eadScriptGrammar?: string;
  mermaidDiagram?: string;
  bodyMarkdown: string;
  configJson?: Record<string, unknown>;
  createdBy?: string;
  createdAt?: number;
  modifiedBy?: string;
}

// Helper to get EAD script from workflow rule config
function workflowRuleScriptFor(rule: EADWorkflowRule): string {
  const cfg = rule.configJson as Record<string, unknown> | undefined;
  if (cfg?.eadScript && typeof cfg.eadScript === "string") {
    return cfg.eadScript;
  }
  return "";
}

function summaryFromRow(row: Record<string, unknown>): EADWorkflowRuleSummary {
  return {
    id: row.id != null ? Number(row.id) : undefined,
    ruleKey: String(row.ruleKey ?? row.rule_key ?? ""),
    ruleName: String(row.ruleName ?? row.rule_name ?? ""),
    ruleDescription: String(row.ruleDescription ?? row.rule_description ?? ""),
    sortNumber: Number(row.sortNumber ?? row.sort_number ?? 0),
    enabled: row.enabled !== false && row.enabled !== 0,
    modifiedAt: Number(row.modifiedAt ?? row.modified_at ?? 0),
  };
}

function ruleFromRow(row: Record<string, unknown>): EADWorkflowRule {
  const summary = summaryFromRow(row);
  return {
    ...summary,
    eadScriptGrammar: String(row.eadScriptGrammar ?? row.ead_script_grammar ?? ""),
    mermaidDiagram: String(row.mermaidDiagram ?? row.mermaid_diagram ?? ""),
    bodyMarkdown: String(row.bodyMarkdown ?? row.body_markdown ?? ""),
    configJson: row.configJson as Record<string, unknown> | undefined,
    createdBy: row.createdBy != null ? String(row.createdBy) : undefined,
    createdAt: row.createdAt != null ? Number(row.createdAt) : undefined,
    modifiedBy: row.modifiedBy != null ? String(row.modifiedBy) : undefined,
  };
}

function normalizeSummaries(rows: Array<EADWorkflowRuleSummary | Record<string, unknown>>): EADWorkflowRuleSummary[] {
  return rows
    .map((row) => summaryFromRow(row as Record<string, unknown>))
    .filter((row) => Boolean(row.ruleKey));
}

@customElement("ead-workflow-rules-view")
export class EADWorkflowRulesView extends LitElement {
  static styles = css`
    :host { display: block; height: 100%; }
    .container { display: flex; height: 100%; flex-direction: column; }
    .breadcrumb { padding: 12px 28px 0; font-size: 13px; color: var(--muted, #8b8d93); }
    .layout { display: flex; flex: 1; min-height: 0; }
    .rule-list {
      width: 400px; min-width: 400px;
      border-right: 1px solid var(--border, #2e2f33);
      overflow-y: auto;
      background: rgba(255,255,255,0.01);
    }
    .rule-list-header { padding: 20px 24px; border-bottom: 1px solid var(--border, #2e2f33); }
    .rule-list-header h2 { margin: 0; font-size: 20px; font-weight: 650; letter-spacing: -0.02em; color: #f8fafc; }
    .rule-item { padding: 16px 24px; border-bottom: 1px solid var(--border, #2e2f33); cursor: pointer; }
    .rule-item:hover { background: rgba(255,255,255,0.03); }
    .rule-item.selected { background: rgba(255,255,255,0.06); border-left: 3px solid var(--accent, #6ea8fe); }
    .rule-item .name { font-size: 16px; font-weight: 560; margin-bottom: 6px; line-height: 1.4; color: #f1f5f9; }
    .rule-item .key { font-size: 13px; color: #94a3b8; font-family: 'JetBrains Mono', 'SF Mono', Monaco, monospace; line-height: 1.45; }
    .rule-item .key-label {
      font-size: 11px; font-weight: 600; color: var(--muted, #8b8d93);
      text-transform: uppercase; letter-spacing: 0.35px; margin-bottom: 4px;
    }
    .rule-item .description {
      font-size: 13px; color: #cbd5e1; line-height: 1.5; margin-top: 8px;
      display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
    }
    .rule-item .meta { display: flex; align-items: center; gap: 8px; margin-top: 8px; font-size: 12px; color: var(--muted, #8b8d93); }
    .rule-item .badge {
      padding: 3px 8px; border-radius: 4px; background: rgba(148, 163, 184, 0.12);
      font-size: 11px; font-weight: 600; letter-spacing: 0.2px;
    }
    .rule-item .badge.disabled { opacity: 0.6; }
    .detail-panel { flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 0; }
    .detail-toolbar {
      display: flex; align-items: center; justify-content: space-between; gap: 16px;
      padding: 16px 24px; border-bottom: 1px solid var(--border, #2e2f33);
      background: rgba(255,255,255,0.02); flex-shrink: 0;
    }
    .detail-toolbar-title h3 {
      margin: 0; font-size: 1.05rem; font-weight: 650; color: #f8fafc; letter-spacing: -0.01em;
    }
    .detail-toolbar-title .subtitle {
      font-size: 13px; color: var(--muted, #8b8d93); margin-top: 4px;
      font-family: 'JetBrains Mono', Monaco, monospace;
    }
    .detail-toolbar-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
    .btn {
      padding: 8px 16px; border-radius: 6px; border: 1px solid var(--border, #2e2f33);
      background: rgba(255,255,255,0.04); color: var(--fg, #e4e5e7);
      font-size: 13px; font-weight: 560; cursor: pointer; letter-spacing: 0.01em;
    }
    .btn:hover { background: rgba(255,255,255,0.08); }
    .btn-primary {
      background: var(--accent, #3b82f6); border-color: var(--accent, #3b82f6); color: #fff;
    }
    .btn-primary:hover { background: var(--accent-hover, #2563eb); }
    .detail-body { flex: 1; min-height: 0; display: flex; flex-direction: column; padding: 20px 24px; }
    .rule-view-scroll { flex: 1; min-height: 0; overflow-y: auto; }
    .rule-view-panel {
      border: 1px solid rgba(148, 163, 184, 0.22);
      border-radius: 10px;
      background: linear-gradient(180deg, rgba(15, 15, 22, 0.98) 0%, rgba(8, 8, 12, 0.99) 100%);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.04);
      padding: 2rem 2.5rem 2.5rem;
    }
    .rule-header { margin-bottom: 1.5rem; }
    .rule-header h3 { margin: 0 0 0.5rem; font-size: 1.35rem; font-weight: 700; color: #f8fafc; }
    .rule-header .key { font-size: 13px; color: var(--accent, #6ea8fe); font-family: 'JetBrains Mono', monospace; }
    .rule-header .description {
      margin-top: 1rem; font-size: 14px; color: #cbd5e1; line-height: 1.6;
    }
    .rule-section { margin-bottom: 2rem; }
    .rule-section h4 {
      margin: 0 0 1rem; font-size: 1.1rem; font-weight: 640; color: #f1f5f9;
      padding-bottom: 0.5rem; border-bottom: 1px solid rgba(148, 163, 184, 0.18);
    }
    .rule-body { font-size: 14px; color: #d4d4d8; line-height: 1.7; white-space: pre-wrap; }
    .rule-body p { margin: 0 0 1em; }
    .rule-body p:last-child { margin-bottom: 0; }
    .empty-state {
      display: grid; place-items: center; height: 100%; color: var(--muted, #8b8d93);
      text-align: center; padding: 40px;
    }
    .empty-state h3 { margin: 0 0 8px; font-size: 18px; color: #f1f5f9; }
    .empty-state p { margin: 0; font-size: 14px; line-height: 1.6; }
    .loading { display: flex; align-items: center; justify-content: center; height: 100%; color: var(--muted, #8b8d93); }
    .loading::after {
      content: ""; width: 24px; height: 24px; border: 2px solid var(--border, #2e2f33);
      border-top-color: var(--accent, #6ea8fe); border-radius: 50%;
      animation: spin 1s linear infinite; margin-left: 12px;
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    .ead-script-section { margin-top: 0.25rem; }
    .ead-script-header {
      display: flex; align-items: center; justify-content: space-between; gap: 12px;
      margin: 1.75rem 0 0.85rem; padding-bottom: 0.35rem;
      border-bottom: 1px solid rgba(148, 163, 184, 0.18);
    }
    .ead-script-header h2 {
      margin: 0; font-size: 1.05rem; font-weight: 650; color: #f1f5f9;
    }
    .ead-script-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
    .btn-sm { padding: 5px 12px; font-size: 12px; }
    .modal-backdrop {
      position: fixed; inset: 0; z-index: 1100;
      background: rgba(0, 0, 0, 0.6);
      display: grid; place-items: center; padding: 12px;
    }
    .diagram-modal {
      width: min(98vw, 1480px);
      max-height: calc(100vh - 24px);
      display: flex; flex-direction: column;
      background: var(--bg, #161920);
      border: 1px solid var(--border, #2e2f33);
      border-radius: 12px;
      box-shadow: 0 22px 48px rgba(0, 0, 0, 0.48);
      overflow: hidden;
    }
    .diagram-modal-header {
      display: flex; align-items: center; justify-content: space-between; gap: 12px;
      padding: 10px 14px; border-bottom: 1px solid var(--border, #2e2f33);
      background: rgba(255, 255, 255, 0.02); flex-shrink: 0;
    }
    .diagram-modal-header h3 { margin: 0; font-size: 16px; color: #f8fafc; }
    .diagram-modal-header .subtitle { font-size: 12px; color: var(--muted, #8b8d93); margin-top: 2px; }
    .diagram-modal-body {
      flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden;
      padding: 0.35rem 0.5rem 0.75rem;
    }
    .diagram-modal-body ead-mermaid-diagram {
      min-height: 0; display: block; width: 100%;
    }
    .field-group { margin-bottom: 18px; }
    .field-group label {
      display: block; font-size: 12px; font-weight: 600; color: var(--muted, #8b8d93);
      margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.35px;
    }
    .field-group .readonly-hint {
      text-transform: none; font-weight: 500; font-size: 11px; color: #94a3b8;
      margin-top: -2px; margin-bottom: 8px;
    }
    .field-group textarea {
      width: 100%; box-sizing: border-box; padding: 10px 12px; border-radius: 8px;
      border: 1px solid var(--border, #2e2f33); background: var(--bg-surface, #222326);
      color: var(--fg, #e4e5e7); font-size: 14px; font-family: inherit; line-height: 1.55;
    }
    .field-group textarea.script {
      min-height: 280px; resize: vertical;
      font-family: 'JetBrains Mono', 'SF Mono', Monaco, monospace;
      font-size: 13px; line-height: 1.5;
    }
    .field-group textarea.prompt {
      min-height: 220px; resize: vertical; line-height: 1.55;
    }
    .preview-stats {
      font-size: 12px; color: #94a3b8; margin-bottom: 10px;
    }
    .preview-stats.warn { color: #ffb74d; }
    .empty-script {
      color: #94a3b8; font-size: 14px; line-height: 1.55; padding: 0.5rem 0 0.25rem;
    }
    .dirty { font-size: 12px; color: #ffb74d; }
    .dirty-inline { font-size: 12px; color: #ffb74d; margin-right: 4px; }
    .badge { display: inline-block; font-size: 11px; padding: 2px 8px; border-radius: 999px; background: rgba(110,168,254,0.15); color: var(--accent, #6ea8fe); margin-left: 8px; vertical-align: middle; }
  `;

  @state()
  private rules: EADWorkflowRuleSummary[] = [];

  @state()
  private selectedRule: EADWorkflowRule | null = null;

  @state()
  private loading = false;

  @state()
  private error: string | null = null;

  @state()
  private showDeleteConfirm = false;

  @state()
  private isSystemAdmin = false;

  @state()
  private editMode = false;

  @state()
  private draft: EADWorkflowRule | null = null;

  @state()
  private saving = false;

  @state()
  private diagramModalOpen = false;

  @state()
  private generateModalOpen = false;

  @state()
  private generatePromptDraft = "";

  @state()
  private generating = false;

  @state()
  private savingPrompt = false;

  @state()
  private diagramRefreshKey = 0;

  @state()
  private scriptPreviewOpen = false;

  @state()
  private pendingGeneratedScript = "";

  @state()
  private savingGeneratedScript = false;

  @state()
  private dirty = false;

  async connectedCallback() {
    super.connectedCallback();
    await this.loadRules();
    await this.checkAdminStatus();
  }

  private async checkAdminStatus() {
    try {
      const response = await hermesClient.request("sessions.list");
      if (response.ok && response.result?.session) {
        this.isSystemAdmin = Boolean(response.result.session.isSystemAdmin);
      }
    } catch {
      this.isSystemAdmin = false;
    }
  }

  private async loadRules() {
    this.loading = true;
    this.error = null;
    try {
      const response = await hermesClient.request<{ result?: EADWorkflowRuleSummary[] }>("ead.workflow_rules.list");
      if (response?.result) {
        this.rules = normalizeSummaries(response.result);
        if (this.rules.length > 0 && !this.selectedRule) {
          await this.selectRule(this.rules[0].ruleKey);
        }
      } else {
        this.error = hermesClient.getLastRequestError() || "Failed to load workflow rules";
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to load workflow rules";
    } finally {
      this.loading = false;
    }
  }

  private async selectRule(ruleKey: string) {
    try {
      const response = await hermesClient.request<{ result?: EADWorkflowRule }>("ead.workflow_rules.get", { ruleKey });
      if (response?.result) {
        this.selectedRule = ruleFromRow(response.result);
        this.draft = this.cloneDraft(this.selectedRule);
        this.editMode = false;
        this.diagramModalOpen = false;
        this.generateModalOpen = false;
        this.scriptPreviewOpen = false;
        this.pendingGeneratedScript = "";
        this.dirty = false;
      } else {
        this.error = hermesClient.getLastRequestError() || "Failed to load rule details";
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to load rule details";
    }
  }

  private cloneDraft(source: EADWorkflowRule): EADWorkflowRule {
    return {
      ...source,
      configJson: source.configJson ? { ...source.configJson } : {},
    };
  }

  private get canEdit(): boolean {
    // Allow editing for all authenticated users (similar to EAD Skills non-view-only skills)
    return true;
  }

  private handleRuleClick(e: Event, ruleKey: string) {
    e.preventDefault();
    if (this.dirty && !confirm("You have unsaved changes. Discard them?")) return;
    this.selectRule(ruleKey);
  }

  private enterEdit(): void {
    if (!this.canEdit) return;
    this.editMode = true;
  }

  private exitEdit(): void {
    if (this.dirty && !confirm("Discard unsaved changes?")) return;
    this.editMode = false;
    if (this.selectedRule) {
      this.draft = this.cloneDraft(this.selectedRule);
      this.dirty = false;
    }
  }

  private liveMermaidSource(rule: EADWorkflowRule): string {
    const script = workflowRuleScriptFor(rule);
    return mermaidFromCurrentEadScript(rule, script);
  }

  private hasDiagramSource(rule: EADWorkflowRule): boolean {
    return true; // Always allow viewing diagram modal
  }

  private openDiagramModal(): void {
    this.diagramRefreshKey += 1;
    this.diagramModalOpen = true;
  }

  private closeDiagramModal(): void {
    this.diagramModalOpen = false;
  }

  private regenerateDiagramFromScript(): void {
    this.diagramRefreshKey += 1;
  }

  private openGenerateModal(): void {
    if (!this.draft) return;
    this.generatePromptDraft = diagramGeneratePromptFor(this.draft);
    this.generateModalOpen = true;
  }

  private closeGenerateModal(): void {
    if (this.generating || this.savingPrompt) return;
    this.generateModalOpen = false;
  }

  private updateGeneratePromptDraft(value: string): void {
    this.generatePromptDraft = value;
  }

  private async saveGeneratePrompt(): Promise<void> {
    if (!this.draft || !this.canEdit) return;
    this.savingPrompt = true;
    this.error = "";
    const prompt = this.generatePromptDraft.trim();
    const response = await hermesClient.request<Record<string, unknown>>("ead.workflow_rules.update", {
      ruleKey: this.draft.ruleKey,
      configJson: {
        ...(this.draft.configJson || {}),
        eadDiagramGeneratePrompt: prompt,
      },
    });
    this.savingPrompt = false;
    if (!response) {
      this.error = hermesClient.getLastRequestError() || "Could not save diagram prompt.";
      return;
    }
    this.selectedRule = ruleFromRow(response);
    this.draft = this.cloneDraft(this.selectedRule);
  }

  private async runGenerateEadScript(): Promise<void> {
    if (!this.draft || !this.canEdit || this.generating) return;
    this.generating = true;
    this.error = "";
    const prompt = this.generatePromptDraft.trim();

    try {
      console.log("Starting EAD script generation...");
      const response = await hermesClient.request<Record<string, unknown>>("ead.workflow_rules.generateEadScript", {
        ruleKey: this.draft.ruleKey,
        generatePrompt: prompt,
        savePrompt: true,
        persist: false,
      });

      console.log("Generation response:", response);

      this.generating = false;

      if (!response) {
        const errorMsg = hermesClient.getLastRequestError() || "EAD script generation failed - no response from server";
        this.error = errorMsg;
        console.error("Generation failed:", errorMsg);
        alert(`Generation Failed: ${errorMsg}`);
        return;
      }

      const generated = String(response.eadScript ?? response.ead_script ?? "").trim();

      if (!generated) {
        const errorMsg = response.error || "EAD script generation returned empty text";
        this.error = errorMsg;
        console.error("Empty generation:", errorMsg);
        alert(`Generation Failed: ${errorMsg}`);
        return;
      }

      console.log("Generation successful, got", generated.length, "characters");
      this.pendingGeneratedScript = generated;
      this.generateModalOpen = false;
      this.scriptPreviewOpen = true;

    } catch (err) {
      this.generating = false;
      const errorMsg = err instanceof Error ? err.message : "Unknown error occurred";
      this.error = errorMsg;
      console.error("Generation error:", err);
      alert(`Generation Error: ${errorMsg}`);
    }
  }

  private cancelGeneratedScript(): void {
    if (this.savingGeneratedScript) return;
    this.scriptPreviewOpen = false;
    this.pendingGeneratedScript = "";
  }

  private async saveGeneratedScript(): Promise<void> {
    if (!this.draft || !this.canEdit || !this.pendingGeneratedScript.trim()) return;
    this.savingGeneratedScript = true;
    this.error = "";
    const script = this.pendingGeneratedScript.trim();
    const mermaid = compileEadScriptToMermaid(script);
    const response = await hermesClient.request<Record<string, unknown>>("ead.workflow_rules.update", {
      ruleKey: this.draft.ruleKey,
      configJson: {
        ...(this.draft.configJson || {}),
        eadScript: script,
        ...(mermaid ? { eadMermaidMap: mermaid } : {}),
      },
    });
    this.savingGeneratedScript = false;
    if (!response) {
      this.error = hermesClient.getLastRequestError() || "Could not save generated EAD Script.";
      return;
    }
    this.selectedRule = ruleFromRow(response);
    this.draft = this.cloneDraft(this.selectedRule);
    this.scriptPreviewOpen = false;
    this.pendingGeneratedScript = "";
    this.diagramRefreshKey += 1;
    this.diagramModalOpen = true;
  }

  private async save(): Promise<void> {
    if (!this.draft) return;
    this.saving = true;
    this.error = "";
    const response = await hermesClient.request<Record<string, unknown>>("ead.workflow_rules.update", {
      ruleKey: this.draft.ruleKey,
      ruleName: this.draft.ruleName,
      ruleDescription: this.draft.ruleDescription,
      bodyMarkdown: this.draft.bodyMarkdown,
      configJson: this.draft.configJson,
    });
    this.saving = false;
    if (!response) {
      this.error = hermesClient.getLastRequestError() || "Save failed.";
      return;
    }
    this.selectedRule = ruleFromRow(response);
    this.draft = this.cloneDraft(this.selectedRule);
    this.editMode = false;
    this.dirty = false;
    await this.loadRules();
  }

  private updateRuleScript(value: string): void {
    if (!this.draft) return;
    this.draft = {
      ...this.draft,
      configJson: { ...(this.draft.configJson || {}), eadScript: value },
    };
    this.dirty = true;
  }

  private renderRuleList() {
    if (this.loading) {
      return html`<div class="loading">Loading workflow rules...</div>`;
    }

    if (this.error) {
      return html`<div class="empty-state">
        <h3>Error Loading Rules</h3>
        <p>${this.error}</p>
      </div>`;
    }

    if (this.rules.length === 0) {
      return html`<div class="empty-state">
        <h3>No Workflow Rules Found</h3>
        <p>No EAD workflow rules are configured.</p>
      </div>`;
    }

    return this.rules.map(
      (rule) => html`
        <div
          class="rule-item ${this.selectedRule?.ruleKey === rule.ruleKey ? "selected" : ""}"
          @click="${(e: Event) => this.handleRuleClick(e, rule.ruleKey)}"
        >
          <div class="name">${rule.ruleName}</div>
          <div class="key-label">Rule Key</div>
          <div class="key">${rule.ruleKey}</div>
          <div class="description">${rule.ruleDescription}</div>
          <div class="meta">
            <span class="badge ${rule.enabled ? "" : "disabled"}">
              ${rule.enabled ? "Enabled" : "Disabled"}
            </span>
            <span class="badge">Sort #${rule.sortNumber}</span>
          </div>
        </div>
      `
    );
  }

  private renderRuleDetail() {
    if (!this.selectedRule) {
      return html`<div class="empty-state">
        <h3>No Rule Selected</h3>
        <p>Select a workflow rule from the list to view details.</p>
      </div>`;
    }

    if (this.editMode) {
      return this._renderEditPanel();
    }

    const rule = this.selectedRule;
    const script = workflowRuleScriptFor(rule);

    return html`
      <div class="rule-view-panel">
        <div class="rule-header">
          <h3>${rule.ruleName}</h3>
          <div class="key">${rule.ruleKey}</div>
          <div class="description">${rule.ruleDescription}</div>
        </div>

        <div class="rule-section">
          <h4>Rule Documentation</h4>
          <div class="rule-body">${rule.bodyMarkdown}</div>
        </div>

        <div class="ead-script-section">
          <div class="ead-script-header">
            <h2>EAD Script</h2>
            <div class="ead-script-actions">
              <button class="btn btn-sm" @click="${() => this.openGenerateModal()}">Generate</button>
              <button
                class="btn btn-sm"
                ?disabled="${!this.hasDiagramSource(rule)}"
                @click="${() => this.openDiagramModal()}"
              >
                View Diagram
              </button>
            </div>
          </div>
          ${script
            ? html`
                <div class="rule-section">
                  <h4>EAD Script Content</h4>
                  <div class="rule-body" style="font-family: 'JetBrains Mono', monospace; white-space: pre-wrap;">${script}</div>
                </div>
              `
            : html`<div class="empty-script">No EAD Script yet. Click Generate to create one from the rule description.</div>`}
        </div>

        ${this.selectedRule.mermaidDiagram ? html`
          <div class="rule-section">
            <h4>Mermaid Diagram</h4>
            <div class="rule-body">
              <ead-mermaid-diagram .source="${this.selectedRule.mermaidDiagram}"></ead-mermaid-diagram>
            </div>
          </div>
        ` : nothing}
      </div>
    `;
  }

  private _renderEditPanel() {
    if (!this.draft) return nothing;

    const script = workflowRuleScriptFor(this.draft);

    return html`
      <div class="rule-view-panel">
        <div class="field-group">
          <label>Workflow Title</label>
          <input
            type="text"
            .value="${this.draft.ruleName}"
            @input="${(e: Event) => this._updateField('ruleName', (e.target as HTMLInputElement).value)}"
            class="btn"
            style="width: 100%; box-sizing: border-box;"
          />
        </div>

        <div class="field-group">
          <label>Workflow Spec</label>
          <textarea
            class="prompt"
            style="min-height: 180px;"
            .value="${this.draft.bodyMarkdown}"
            @input="${(e: Event) => this._updateField('bodyMarkdown', (e.target as HTMLTextAreaElement).value)}"
          ></textarea>
        </div>

        <div class="field-group">
          <label>Workflow Script</label>
          <div class="ead-script-header" style="margin-top: 0; margin-bottom: 8px; border-bottom: none; padding-bottom: 0;">
            <div class="ead-script-actions">
              <button class="btn btn-sm" @click="${() => this.openGenerateModal()}">Generate</button>
              <button
                class="btn btn-sm"
                ?disabled="${!this.hasDiagramSource(this.draft)}"
                @click="${() => this.openDiagramModal()}"
              >
                View Diagram
              </button>
            </div>
          </div>
          <textarea
            class="script"
            .value="${script}"
            @input="${(e: Event) => this.updateRuleScript((e.target as HTMLTextAreaElement).value)}"
          ></textarea>
        </div>

        <div class="detail-toolbar-actions" style="justify-content: flex-end; padding-top: 20px; border-top: 1px solid var(--border, #2e2f33); margin-top: 20px;">
          <button class="btn" @click="${() => this.exitEdit()}">
            ${this.dirty ? "Cancel" : "Close"}
          </button>
          ${this.dirty ? html`
            <button class="btn btn-primary" ?disabled="${this.saving}" @click="${() => void this.save()}">
              ${this.saving ? "Saving…" : "Save"}
            </button>
          ` : nothing}
        </div>
      </div>
    `;
  }

  private _updateField(field: keyof EADWorkflowRule, value: string): void {
    if (!this.draft) return;
    this.draft = { ...this.draft, [field]: value };
    this.dirty = true;
  }

  private renderDiagramModal() {
    if (!this.diagramModalOpen || !this.draft) return nothing;
    const mermaidSource = this.liveMermaidSource(this.draft);
    const hasScript = Boolean(workflowRuleScriptFor(this.draft).trim());
    return html`
      <div class="modal-backdrop" @click="${() => this.closeDiagramModal()}">
        <div
          class="diagram-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ead-diagram-modal-title"
          @click="${(e: Event) => e.stopPropagation()}"
        >
          <div class="diagram-modal-header">
            <div>
              <h3 id="ead-diagram-modal-title">EAD Workflow Diagram</h3>
              <div class="subtitle">Workflow Rule: ${this.draft.ruleKey}</div>
            </div>
            <div class="detail-toolbar-actions">
              <button
                class="btn btn-sm"
                ?disabled="${!hasScript}"
                @click="${() => this.regenerateDiagramFromScript()}"
              >
                Regenerate
              </button>
              <button class="btn" @click="${() => this.closeDiagramModal()}">Close</button>
            </div>
          </div>
          <div class="diagram-modal-body">
            <ead-mermaid-diagram
              .source="${mermaidSource}"
              .showHeading="${false}"
              .showSourceToggle="${true}"
              .embedded="${true}"
              .refreshKey="${this.diagramRefreshKey}"
            ></ead-mermaid-diagram>
          </div>
        </div>
      </div>
    `;
  }

  private renderGenerateModal() {
    if (!this.generateModalOpen || !this.draft) return nothing;
    return html`
      <div class="modal-backdrop" @click="${() => this.closeGenerateModal()}">
        <div
          class="diagram-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ead-generate-modal-title"
          @click="${(e: Event) => e.stopPropagation()}"
        >
          <div class="diagram-modal-header">
            <div>
              <h3 id="ead-generate-modal-title">Generate EAD Workflow Diagram</h3>
              <div class="subtitle">Workflow Rule: ${this.draft.ruleKey}</div>
            </div>
            <button class="btn" ?disabled="${this.generating || this.savingPrompt}" @click="${() => this.closeGenerateModal()}">
              Close
            </button>
          </div>
          <div class="diagram-modal-body">
            <div class="field-group">
              <label>AI Prompt for EAD Workflow Diagram</label>
              <div class="readonly-hint">Saved per rule. Edit before generating the EAD Script.</div>
              <textarea
                class="prompt"
                .value="${this.generatePromptDraft}"
                ?disabled="${!this.canEdit || this.generating || this.savingPrompt}"
                @input="${(e: Event) => this.updateGeneratePromptDraft((e.target as HTMLTextAreaElement).value)}"
              ></textarea>
            </div>
            <div class="detail-toolbar-actions" style="justify-content: flex-end; padding-top: 4px;">
              <button class="btn" ?disabled="${this.generating || this.savingPrompt}" @click="${() => this.closeGenerateModal()}">
                Cancel
              </button>
              ${this.canEdit
                ? html`
                    <button
                      class="btn"
                      ?disabled="${this.generating || this.savingPrompt || !this.generatePromptDraft.trim()}"
                      @click="${() => void this.saveGeneratePrompt()}"
                    >
                      ${this.savingPrompt ? "Saving…" : "Save Prompt"}
                    </button>
                    <button
                      class="btn btn-primary"
                      ?disabled="${this.generating || this.savingPrompt || !this.generatePromptDraft.trim()}"
                      @click="${() => void this.runGenerateEadScript()}"
                    >
                      ${this.generating ? "Generating…" : "Generate"}
                    </button>
                  `
                : nothing}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderScriptPreviewModal() {
    if (!this.scriptPreviewOpen || !this.pendingGeneratedScript) return nothing;
    const stats = countEadScriptElements(this.pendingGeneratedScript);
    const overLimit = stats.totalEntities > 10 || stats.actionCount > 12;
    const previewMermaid = compileEadScriptToMermaid(this.pendingGeneratedScript);
    return html`
      <div class="modal-backdrop" @click="${() => this.cancelGeneratedScript()}">
        <div
          class="diagram-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="ead-script-preview-title"
          @click="${(e: Event) => e.stopPropagation()}"
        >
          <div class="diagram-modal-header">
            <div>
              <h3 id="ead-script-preview-title">Generated EAD Workflow Script</h3>
              <div class="subtitle">Review before saving · ${this.draft?.ruleKey || ""}</div>
            </div>
            <button class="btn" ?disabled="${this.savingGeneratedScript}" @click="${() => this.cancelGeneratedScript()}">
              Close
            </button>
          </div>
          <div class="diagram-modal-body">
            <div class="preview-stats ${overLimit ? "warn" : ""}">
              Entities: ${stats.totalEntities}/10 · Actions: ${stats.actionCount}/12
              ${overLimit ? " · Over simplicity limits — edit after save or regenerate." : ""}
            </div>
            <div class="field-group">
              <label>Generated EAD Workflow Script</label>
              <textarea class="script" readonly .value="${this.pendingGeneratedScript}"></textarea>
            </div>
            ${previewMermaid
              ? html`
                  <ead-mermaid-diagram
                    .source="${previewMermaid}"
                    .showHeading="${false}"
                    .showSourceToggle="${true}"
                    .embedded="${true}"
                    .refreshKey="${this.diagramRefreshKey}"
                  ></ead-mermaid-diagram>
                `
              : nothing}
            <div class="detail-toolbar-actions" style="justify-content: flex-end; padding-top: 12px;">
              <button class="btn" ?disabled="${this.savingGeneratedScript}" @click="${() => this.cancelGeneratedScript()}">
                Cancel
              </button>
              <button
                class="btn btn-primary"
                ?disabled="${this.savingGeneratedScript}"
                @click="${() => void this.saveGeneratedScript()}"
              >
                ${this.savingGeneratedScript ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <div class="container">
        <div class="breadcrumb">EAD Explore / EAD Workflow Rules</div>
        <div class="layout">
          <div class="rule-list">
            <div class="rule-list-header">
              <h2>EAD Workflow Rules</h2>
            </div>
            ${this.renderRuleList()}
          </div>
          <div class="detail-panel">
            <div class="detail-toolbar">
              <div class="detail-toolbar-title">
                <h3>${this.editMode && this.dirty ? html`<span class="dirty-inline">●</span>` : nothing}${this.selectedRule?.ruleName || "Workflow Rules"}</h3>
                <div class="subtitle">${this.selectedRule?.ruleKey || ""}</div>
              </div>
              <div class="detail-toolbar-actions">
                ${this.editMode ? html`
                  <button class="btn" @click="${() => this.exitEdit()}">
                    ${this.dirty ? "Cancel" : "Close"}
                  </button>
                  ${this.dirty ? html`
                    <button class="btn btn-primary" ?disabled="${this.saving}" @click="${() => void this.save()}">
                      ${this.saving ? "Saving…" : "Save"}
                    </button>
                  ` : nothing}
                ` : html`
                  ${this.canEdit ? html`
                    <button class="btn" @click="${() => this.enterEdit()}">Edit</button>
                    <button class="btn" @click="${(e: Event) => e.stopPropagation()}">...</button>
                  ` : nothing}
                `}
              </div>
            </div>
            <div class="detail-body">
              <div class="rule-view-scroll">
                ${this.renderRuleDetail()}
              </div>
            </div>
          </div>
        </div>
        ${this.renderDiagramModal()}
        ${this.renderGenerateModal()}
        ${this.renderScriptPreviewModal()}
      </div>
    `;
  }
}